import type { CatalogueLookup } from '$lib/editor'
import type { Plan } from '$lib/types'

/**
 * Quick footprint summary for the toolbar. Phase 2.5 surfaces only the
 * basic counts a player wants at a glance — Phase 4 added the production
 * stats engine alongside it.
 */
export interface FootprintStats {
  /** Number of unlocked-area rectangles. Each in the export typically
   *  maps to one in-game expansion, though adjacent rectangles can be
   *  merged during normalisation. We surface it as "expansions" because
   *  that's the player-facing term. */
  expansions: number
  /** Total unlocked tile count (sum of width × length over all rects). */
  totalUnlocked: number
  /** Total tiles covered by placed buildings. Unresolved entries fall
   *  back to 1×1, matching renderer + validator behaviour. */
  occupied: number
  /** totalUnlocked − occupied, floored at 0. (Negative would mean the
   *  buildings overflow the unlocked area; clamping is friendlier for
   *  the toolbar than showing a negative.) */
  free: number
  /** Integer percent of unlocked tiles that are occupied. 0 when the
   *  unlocked area is empty. */
  usedPercent: number
  /** plan.buildings.length — surfaced so the toolbar doesn't recompute. */
  buildingCount: number
  /** Total road tiles placed (sum of width × height over street-type
   *  buildings). Equals the count for the typical 1×1 street, but
   *  multi-tile road placements would expand this. Drives the toolbar's
   *  road counter — players think in tiles, not in placements. */
  roadCount: number
}

/**
 * Compute toolbar-friendly footprint stats. Pure: takes a lookup
 * callback so it doesn't depend on the catalogue module directly.
 * Off-grid buildings (Friends Tavern, Outposts, hub parts) are
 * skipped — they're filtered out of plan.buildings during import,
 * but the check is defensive so a future import path that lets one
 * through doesn't poison the toolbar count.
 */
export function computeFootprint(plan: Plan, lookup: CatalogueLookup): FootprintStats {
  let totalUnlocked = 0
  for (const a of plan.unlockedAreas) {
    totalUnlocked += a.width * a.length
  }

  let occupied = 0
  let roadCount = 0
  for (const b of plan.buildings) {
    const r = lookup(b.cityentityId)
    if (r?.isOffGrid) continue
    const w = r && r.width > 0 ? r.width : 1
    const h = r && r.height > 0 ? r.height : 1
    occupied += w * h
    if (r?.isRoad) roadCount += w * h
  }

  const free = Math.max(0, totalUnlocked - occupied)
  const usedPercent =
    totalUnlocked > 0 ? Math.round((occupied / totalUnlocked) * 100) : 0

  return {
    expansions: plan.unlockedAreas.length,
    totalUnlocked,
    occupied,
    free,
    usedPercent,
    buildingCount: plan.buildings.length,
    roadCount,
  }
}
